import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type StaffAccount, type OrgRole, type StaffOrg } from "../lib/api.ts";
import { useStaff } from "../lib/staff.ts";
import { useSession } from "../lib/auth.ts";
import { useSortableTable, SortHeader } from "../components/SortableTable.tsx";
import { assessPassword, PASSWORD_MIN_LENGTH } from "../lib/password.ts";

export function StaffAccountsPage() {
  const qc = useQueryClient();
  const { role } = useStaff();
  const { data: session } = useSession();
  const myUserId = session?.user?.id;
  const isAdmin = role === "admin";

  const accounts = useQuery({
    queryKey: ["staff", "accounts"],
    queryFn: api.staffListAccounts,
  });
  const sortable = useSortableTable(accounts.data?.accounts ?? [], {
    email:       (a) => a.email,
    name:        (a) => a.name,
    memberships: (a) => a.memberships.length + (a.staffRole ? 1000 : 0), // staff sorts to top
    createdAt:   (a) => a.createdAt,
    lastSeenAt:  (a) => a.lastSeenAt ?? "",  // empty string sorts before any ISO date
    blocked:     (a) => (a.blockedAt ? 1 : 0),
  }, "createdAt", "desc");
  const orgs = useQuery({
    queryKey: ["staff", "orgs"],
    queryFn: api.staffListOrgs,
    enabled: isAdmin,
  });

  const [confirmDelete, setConfirmDelete] = useState<StaffAccount | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const invalidateAccounts = () =>
    qc.invalidateQueries({ queryKey: ["staff", "accounts"] });

  const block = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string | null }) =>
      api.staffBlockAccount(id, reason),
    onSuccess: invalidateAccounts,
  });
  const unblock = useMutation({
    mutationFn: (id: string) => api.staffUnblockAccount(id),
    onSuccess: invalidateAccounts,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.staffDeleteAccount(id),
    onSuccess: () => { setConfirmDelete(null); invalidateAccounts(); },
  });
  const create = useMutation({
    mutationFn: api.staffCreateAccount,
    onSuccess: (data) => {
      setShowCreate(false);
      setActionMsg(
        data.mode === "invite"
          ? `Invited ${data.email}. Reset email sent (check wrangler logs in dev).`
          : `Created ${data.email}.`,
      );
      invalidateAccounts();
    },
  });
  const resetPwd = useMutation({
    mutationFn: (userId: string) => api.staffResetPassword(userId),
    onSuccess: (data) =>
      setActionMsg(`Reset email queued for ${data.email} (check wrangler logs in dev).`),
  });
  const addMembership = useMutation({
    mutationFn: ({ userId, orgId, orgRole }: { userId: string; orgId: string; orgRole: OrgRole }) =>
      api.staffAddMembership(userId, orgId, orgRole),
    onSuccess: invalidateAccounts,
  });
  const removeMembership = useMutation({
    mutationFn: ({ userId, orgId }: { userId: string; orgId: string }) =>
      api.staffRemoveMembership(userId, orgId),
    onSuccess: invalidateAccounts,
  });

  const lastErr = (
    block.error || unblock.error || remove.error ||
    resetPwd.error || addMembership.error || removeMembership.error
  ) as Error | undefined;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <h1>User accounts</h1>
        {isAdmin && (
          <button onClick={() => { setShowCreate(true); create.reset(); }}>
            Create account
          </button>
        )}
      </div>
      <p className="muted">
        Every signed-up user. Block to revoke sign-in (sessions are killed
        immediately); delete is enabled once blocked. Manage to assign or
        remove org memberships.
      </p>

      {actionMsg && (
        <p style={{ color: "var(--c-success)", marginTop: "0.5rem" }}>{actionMsg}</p>
      )}
      {lastErr && (
        <p className="err" style={{ marginTop: "0.5rem" }}>{lastErr.message}</p>
      )}

      {accounts.isLoading && <p className="muted">Loading…</p>}
      {accounts.error && (
        <p className="err">Couldn't load — {(accounts.error as Error).message}</p>
      )}
      {accounts.data && accounts.data.accounts.length > 0 && (
        <table>
          <thead>
            <tr>
              <SortHeader label="Email"       columnKey="email"       sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Name"        columnKey="name"        sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Memberships" columnKey="memberships" sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Created"     columnKey="createdAt"   sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Last seen"   columnKey="lastSeenAt"  sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              <SortHeader label="Status"      columnKey="blocked"     sortKey={sortable.sortKey} sortDir={sortable.sortDir} onSort={sortable.handleSort} />
              {isAdmin && <th></th>}
            </tr>
          </thead>
          <tbody>
            {sortable.sorted.map((a) => {
              const isMe = a.id === myUserId;
              const isExpanded = expanded === a.id;
              return (
                <>
                  <tr key={a.id} style={a.blockedAt ? { opacity: 0.6 } : undefined}>
                    <td>{a.email}</td>
                    <td>{a.name ?? <span className="muted">—</span>}</td>
                    <td>
                      {a.staffRole && (
                        <span className="pill pass" style={{ marginRight: "0.4rem" }}>
                          staff: {a.staffRole}
                        </span>
                      )}
                      {a.memberships.length === 0 && !a.staffRole && (
                        <span className="muted">none</span>
                      )}
                      {a.memberships.map((m) => (
                        <span
                          key={m.orgId}
                          className="pill cancelled"
                          style={{ marginRight: "0.4rem" }}
                          title={`role: ${m.role}`}
                        >
                          {m.orgName ?? m.orgId.slice(0, 6)} · {m.role}
                        </span>
                      ))}
                    </td>
                    <td>{new Date(a.createdAt).toLocaleDateString()}</td>
                    <td title={a.lastSeenAt ? new Date(a.lastSeenAt).toLocaleString() : undefined}>
                      {a.lastSeenAt
                        ? formatLastSeen(a.lastSeenAt)
                        : <span className="muted">—</span>}
                    </td>
                    <td>
                      {a.blockedAt
                        ? <span className="pill fail" title={a.blockedReason ?? undefined}>Blocked</span>
                        : <span className="muted">Active</span>}
                    </td>
                    {isAdmin && (
                      <td style={{ whiteSpace: "nowrap" }}>
                        <button
                          className="secondary"
                          onClick={() => setExpanded(isExpanded ? null : a.id)}
                        >
                          {isExpanded ? "Close" : "Manage"}
                        </button>
                      </td>
                    )}
                  </tr>
                  {isExpanded && isAdmin && (
                    <tr>
                      <td colSpan={7} style={{ background: "var(--c-bg)" }}>
                        <ManageRow
                          account={a}
                          isMe={isMe}
                          orgs={orgs.data?.orgs ?? []}
                          onBlock={(reason) => block.mutate({ id: a.id, reason })}
                          onUnblock={() => unblock.mutate(a.id)}
                          onDelete={() => setConfirmDelete(a)}
                          onResetPassword={() => resetPwd.mutate(a.id)}
                          onAddMembership={(orgId, orgRole) =>
                            addMembership.mutate({ userId: a.id, orgId, orgRole })}
                          onRemoveMembership={(orgId) =>
                            removeMembership.mutate({ userId: a.id, orgId })}
                        />
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <DeleteAccountConfirm
          account={confirmDelete}
          pending={remove.isPending}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => remove.mutate(confirmDelete.id)}
        />
      )}

      {showCreate && (
        <CreateAccountModal
          pending={create.isPending}
          error={(create.error as Error | null)?.message ?? null}
          onCancel={() => { setShowCreate(false); create.reset(); }}
          onSubmit={(input) => create.mutate(input)}
        />
      )}
    </>
  );
}

// Relative "last seen" stamp. Better-Auth refreshes session.updatedAt
// once per updateAge window (~1 day) so finer granularity than that
// would be misleading — anything within the last 24h is rendered as
// "today" rather than e.g. "3h ago".
function formatLastSeen(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "—";
  const days = Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
  if (days <= 0)  return "today";
  if (days === 1) return "yesterday";
  if (days < 7)   return `${days} days ago`;
  if (days < 30)  return `${Math.floor(days / 7)} weeks ago`;
  return new Date(iso).toLocaleDateString();
}

// -----------------------------------------------------------------------------
// Inline expanded row: memberships list + add form + danger zone.
// -----------------------------------------------------------------------------

function ManageRow({
  account, isMe, orgs,
  onBlock, onUnblock, onDelete, onResetPassword,
  onAddMembership, onRemoveMembership,
}: {
  account: StaffAccount;
  isMe: boolean;
  orgs: StaffOrg[];
  onBlock: (reason: string | null) => void;
  onUnblock: () => void;
  onDelete: () => void;
  onResetPassword: () => void;
  onAddMembership: (orgId: string, role: OrgRole) => void;
  onRemoveMembership: (orgId: string) => void;
}) {
  const memberOrgIds = new Set(account.memberships.map((m) => m.orgId));
  const availableOrgs = orgs.filter((o) => !memberOrgIds.has(o.id));
  const [pickOrg, setPickOrg] = useState<string>("");
  const [pickRole, setPickRole] = useState<OrgRole>("member");

  return (
    <div style={{ padding: "1rem", display: "grid", gap: "1rem" }}>
      <section>
        <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Memberships</h3>
        {account.memberships.length === 0 && (
          <p className="muted" style={{ marginTop: 0 }}>No memberships.</p>
        )}
        {account.memberships.length > 0 && (
          <table style={{ marginTop: 0 }}>
            <thead>
              <tr>
                <th>Org</th>
                <th>Role</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {account.memberships.map((m) => (
                <tr key={m.orgId}>
                  <td>{m.orgName ?? <code className="muted">{m.orgId}</code>}</td>
                  <td>{m.role}</td>
                  <td>
                    <button
                      className="secondary"
                      onClick={() => onRemoveMembership(m.orgId)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {availableOrgs.length > 0 && (
          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
            <select
              value={pickOrg}
              onChange={(e) => setPickOrg(e.target.value)}
              aria-label="Pick org"
              style={{ flex: "1 1 200px" }}
            >
              <option value="">Pick org…</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
            <select
              value={pickRole}
              onChange={(e) => setPickRole(e.target.value as OrgRole)}
              aria-label="Role"
              style={{ flex: "0 0 140px" }}
            >
              <option value="member">member</option>
              <option value="admin">admin</option>
              <option value="owner">owner</option>
            </select>
            <button
              disabled={!pickOrg}
              onClick={() => {
                if (pickOrg) {
                  onAddMembership(pickOrg, pickRole);
                  setPickOrg("");
                }
              }}
            >
              Assign
            </button>
          </div>
        )}
      </section>

      <section>
        <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Account actions</h3>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            className="secondary"
            onClick={onResetPassword}
            disabled={isMe}
          >
            Reset password
          </button>
          {isMe ? (
            <span className="muted" style={{ alignSelf: "center" }}>
              You can't block or delete your own account.
            </span>
          ) : !account.blockedAt ? (
            <button
              className="secondary"
              onClick={() => {
                const reason = window.prompt(`Block ${account.email}? Optional reason:`);
                if (reason !== null) onBlock(reason.trim() || null);
              }}
            >
              Block sign-in
            </button>
          ) : (
            <>
              <button className="secondary" onClick={onUnblock}>Unblock</button>
              <button className="danger" onClick={onDelete}>Delete account…</button>
            </>
          )}
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Create-account modal — toggle between Email+Password and Email+Invite.
// -----------------------------------------------------------------------------

function CreateAccountModal({
  pending, error, onCancel, onSubmit,
}: {
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (input: {
    email: string;
    name?: string;
    mode: "password" | "invite";
    password?: string;
  }) => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [mode, setMode] = useState<"password" | "invite">("invite");
  const [password, setPassword] = useState("");

  const pwAssessment = assessPassword(password);
  const canSubmit =
    email.includes("@") &&
    (mode === "invite" || (mode === "password" && pwAssessment.ok));

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: "100%" }}>
        <h2 style={{ marginTop: 0 }}>Create user account</h2>

        <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
          <button
            className={mode === "invite" ? undefined : "secondary"}
            onClick={() => setMode("invite")}
            type="button"
          >
            Email invite
          </button>
          <button
            className={mode === "password" ? undefined : "secondary"}
            onClick={() => setMode("password")}
            type="button"
          >
            Email + password
          </button>
        </div>

        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          <span style={{ display: "block", marginBottom: "0.25rem" }}>Email</span>
          <input
            autoFocus
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value.trim())}
            placeholder="user@example.com"
          />
        </label>
        <label style={{ display: "block", marginBottom: "0.75rem" }}>
          <span style={{ display: "block", marginBottom: "0.25rem" }}>
            Name <span className="muted">(optional)</span>
          </span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Defaults to the email's local part"
          />
        </label>

        {mode === "password" ? (
          <label style={{ display: "block", marginBottom: "0.75rem" }}>
            <span style={{ display: "block", marginBottom: "0.25rem" }}>
              Password <span className="muted">(min {PASSWORD_MIN_LENGTH} chars; mix 3 of: a-z, A-Z, 0-9, symbols)</span>
            </span>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Tell the user out-of-band — we don't email it."
            />
            {pwAssessment.hint && (
              <div className="err" style={{ marginTop: "0.25rem" }}>{pwAssessment.hint}</div>
            )}
          </label>
        ) : (
          <p className="muted" style={{ marginBottom: "0.75rem" }}>
            We'll create the account and send a password-reset link to this
            email. In dev, the link is logged to the wrangler console.
          </p>
        )}

        {error && <p className="err">{error}</p>}
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>Cancel</button>
          <button
            disabled={!canSubmit || pending}
            onClick={() =>
              onSubmit({
                email: email.toLowerCase(),
                name: name.trim() || undefined,
                mode,
                password: mode === "password" ? password : undefined,
              })
            }
          >
            {pending ? "Creating…" : mode === "password" ? "Create" : "Create + invite"}
          </button>
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Hard-delete confirmation modal.
// -----------------------------------------------------------------------------

function DeleteAccountConfirm({
  account, pending, onCancel, onConfirm,
}: {
  account: StaffAccount;
  pending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState("");
  const matches = typed === account.email;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div className="card" style={{ maxWidth: 480, width: "100%" }}>
        <h2 style={{ marginTop: 0 }}>Delete user account</h2>
        <p>
          This permanently removes <strong>{account.email}</strong> and all of
          their authentication data: sessions, OAuth links, organisation
          memberships, invitations they sent, and any staff role.
        </p>
        <p className="muted">
          Org-owned data (devices, tests) stays with the org. Other admins
          remain in any orgs this user belonged to.
        </p>
        <p>Type the email to confirm:</p>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={account.email}
          aria-label="Confirm email"
        />
        <div style={{ marginTop: "1rem", display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <button className="secondary" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            className="danger"
            disabled={!matches || pending}
            onClick={onConfirm}
          >
            {pending ? "Deleting…" : "Delete permanently"}
          </button>
        </div>
      </div>
    </div>
  );
}
